/**
 * Microsoft-branded sign-in button.
 *
 * Conforms to Microsoft's brand guidelines for "Sign in with Microsoft":
 *   - White background, dark text on light themes (the dark variant is
 *     unused here; portals are light-themed today).
 *   - 4-square Microsoft logo (orange, green, blue, yellow) at left.
 *   - Sans-serif text, exact phrase "Sign in with Microsoft".
 *   - Min 41px tall target; visible focus ring; no logo recoloring.
 *
 * Used by the portal landing page (replaces the generic header button
 * for the primary CTA) and by future F18 onboarding wizard.
 */

import { type ReactElement } from 'react';

interface MicrosoftSignInButtonProps {
  onClick: () => void;
  disabled?: boolean;
  label?: string;
  /** Optional small secondary line below the brand label (e.g. tenant name). */
  subLabel?: string;
  'data-testid'?: string;
}

export function MicrosoftSignInButton({
  onClick,
  disabled = false,
  label = 'Sign in with Microsoft',
  subLabel,
  ...rest
}: MicrosoftSignInButtonProps): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={rest['data-testid']}
      className={
        'group inline-flex items-center gap-3 rounded-sm border border-gray-400 bg-white px-5 py-3 ' +
        'text-base font-medium text-gray-900 shadow-sm ' +
        'hover:border-gray-700 hover:bg-gray-50 ' +
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-700 ' +
        'disabled:cursor-not-allowed disabled:opacity-60 ' +
        'transition-colors duration-150 min-h-[48px]'
      }
      aria-label={subLabel ? `${label}. ${subLabel}` : label}
    >
      <MicrosoftLogoMark />
      <span className="flex flex-col items-start leading-tight">
        <span>{label}</span>
        {subLabel ? (
          <span className="text-xs font-normal text-gray-600">{subLabel}</span>
        ) : null}
      </span>
    </button>
  );
}

/**
 * The 4-square Microsoft logo mark. SVG so it scales crisply. Colors are
 * the canonical Microsoft brand hex values; do not recolor or restyle.
 */
function MicrosoftLogoMark(): ReactElement {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 21 21"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="shrink-0"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}
